import { useState, useEffect, useRef } from "react";
import Card from "components/card";
import { adminAuthService } from "services/adminAuthService";
import { r2Service } from "services/r2Service";
import { AiOutlineCamera } from "react-icons/ai";
import { DEFAULT_USER_AVATAR } from "components/ui/UserAvatar";
import { useAppLanguage } from "hooks/useAppLanguage";

interface Profile {
  id: number;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  image?: string | null;
  image_url?: string | null;
  is_active?: boolean;
  roles?: string[];
  permissions?: string[];
}

const ProfilePage = () => {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchProfile = async () => {
    try {
      const data = await adminAuthService.getProfile();
      setProfile(data);
      setName(data?.name || "");
      setPhone(data?.phone || "");
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const getImageUrl = (imagePath: string | undefined | null): string => {
    if (!imagePath || imagePath === "null" || imagePath === "undefined") {
      return DEFAULT_USER_AVATAR;
    }
    if (imagePath.startsWith("http") || imagePath.startsWith("data:image/") || imagePath.startsWith("blob:")) {
      return imagePath;
    }
    return DEFAULT_USER_AVATAR;
  };

  const buildProfilePayload = (nextImage = profile?.image) => ({
    name: name.trim() || profile?.name || profile?.phone || "Admin",
    email: profile?.email || "",
    phone: phone || "",
    image: nextImage || "",
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setMessage(null);

    try {
      const result = await r2Service.uploadWithPresign(file, "avatars");
      const updated = await adminAuthService.updateProfile(buildProfilePayload(result.key));
      setProfile(updated);
      setMessage({ type: "success", text: isArabic ? "تم تحديث صورة الملف الشخصي." : "Profile image updated!" });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error("Image upload failed:", error);
      setMessage({
        type: "error",
        text: error?.message || (isArabic ? "فشل رفع الصورة" : "Failed to upload image"),
      });
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ type: "error", text: isArabic ? "الاسم مطلوب" : "Name is required" });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const response = await adminAuthService.updateProfile(buildProfilePayload());
      setProfile(response);
      setMessage({ type: "success", text: isArabic ? "تم تحديث الملف الشخصي بنجاح." : "Profile updated successfully!" });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error("Failed to update profile:", error);
      setMessage({
        type: "error",
        text: error?.response?.data?.message || (isArabic ? "فشل تحديث الملف الشخصي" : "Failed to update profile"),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-3 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            {isArabic ? "إعدادات الملف الشخصي" : "Profile Settings"}
          </h1>
          <p className="text-gray-500">
            {isArabic ? "إدارة إعدادات حسابك" : "Manage your account settings"}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <Card extra="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-4">
                <div
                  className="h-32 w-32 overflow-hidden rounded-full border-4 border-white shadow-lg"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingImage ? (
                    <div className="flex h-full w-full items-center justify-center bg-gray-900/50">
                      <div className="h-8 w-8 animate-spin rounded-full border-3 border-white border-t-transparent" />
                    </div>
                  ) : (
                    <img
                      src={getImageUrl(profile?.image_url || profile?.image)}
                      alt={isArabic ? "الصورة الشخصية" : "Profile"}
                      className="h-full w-full cursor-pointer object-cover"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.src !== DEFAULT_USER_AVATAR) {
                          img.src = DEFAULT_USER_AVATAR;
                        }
                      }}
                    />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white shadow-lg transition hover:bg-brand-600"
                >
                  <AiOutlineCamera size={18} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>

              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                {profile?.name || profile?.phone || (isArabic ? "مدير" : "Admin")}
              </h3>
              <p className="text-sm text-gray-500">{profile?.phone || "—"}</p>

              <div className="mt-6 w-full space-y-3">
                <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-navy-700">
                  <span className="text-sm text-gray-500">{isArabic ? "الحالة" : "Status"}</span>
                  <span className={`text-sm font-medium ${profile?.is_active ? "text-green-500" : "text-red-500"}`}>
                    {profile?.is_active ? (isArabic ? "نشط" : "Active") : isArabic ? "غير نشط" : "Inactive"}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-navy-700">
                  <span className="text-sm text-gray-500">{isArabic ? "الأدوار" : "Roles"}</span>
                  <span className="text-sm font-medium text-navy-700 dark:text-white">
                    {profile?.roles?.join(", ") || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-navy-700">
                  <span className="text-sm text-gray-500">ID</span>
                  <span className="text-sm font-medium text-navy-700 dark:text-white">#{profile?.id}</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card extra="p-6">
            <h3 className="mb-6 text-lg font-bold text-navy-700 dark:text-white">
              {isArabic ? "البيانات الشخصية" : "Personal Information"}
            </h3>

            {message && (
              <div
                className={`mb-4 rounded-lg px-4 py-3 text-sm ${
                  message.type === "success"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {message.text}
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "الاسم الكامل *" : "Full Name *"}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={isArabic ? "أدخل اسمك" : "Enter your name"}
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "البريد الإلكتروني" : "Email Address"}
                </label>
                <input
                  type="email"
                  value={profile?.email || "—"}
                  disabled
                  className="h-12 w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-500 dark:border-white/10 dark:bg-navy-800 dark:text-white/50"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  {isArabic ? "رقم الهاتف" : "Phone Number"}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={isArabic ? "أدخل رقم الهاتف" : "Enter your phone number"}
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white"
                />
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                  className="linear flex items-center justify-center rounded-xl bg-brand-500 px-8 py-3 text-base font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    isArabic ? "حفظ التعديلات" : "Save Changes"
                  )}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
